import { useState, useCallback, useMemo } from 'react'
import { PrismBanner, PrismButton } from '@prism-ui/react'
import useFetch from '../../../../hooks/useFetch'
import { useEmailReminderSocket } from '../../../../hooks/useEmailReminderSocket'
import EmailModal from './EmailModal'
import { Option, EmailReminder } from './types'
import EmailReminderItem from './EmailReminderItem'
import ManageUrls from './ManageUrls'
import './EmailReminders.css'
import { toast } from '../../../Toaster/toast'
import { DateTime } from 'luxon'
import AdminPane from '../../AdminPane';

const EmailReminders = () => {
  const {data:recipientOptions=[]} = useFetch<Option[]>('/api/email_reminders/recipient_options')
  const {data:chapterOptions=[]} = useFetch<Option[]>('/api/email_reminders/chapter_options')
  const {data:urlOptions=[], refresh:refreshUrls} = useFetch<{id: number, name: string, url: string}[]>('/api/email_urls')

  // Memoize transformed URL options to prevent unnecessary re-renders
  const transformedUrlOptions = useMemo(() => 
    urlOptions.map(u => ({ value: u.id.toString(), label: u.name, url: u.url })),
    [urlOptions]
  )

  const [activeTab, setActiveTab] = useState<'schedule' | 'urls'>('schedule')
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<number>()
  const [showSent, setShowSent] = useState(false)

  const {data:all=[], refresh} = useFetch<EmailReminder[]>('/api/email_reminders')
  
  // WebSocket real-time updates - only refresh when tab is active
  const handleEmailReminderEvent = useCallback(() => {
    // Refresh the list when any email reminder event occurs
    refresh();
  }, [refresh]);
  
  useEmailReminderSocket({
    onEvent: handleEmailReminderEvent,
    enabled: activeTab === 'schedule' // Only listen when on the schedule tab
  });
  
  const shown = all.filter(x=> showSent ? !!x.sentOn : !x.sentOn)
  const anyUnsentToday = undefined != all.find(x=>{
    if (x.sentOn != undefined) return false
    const t = DateTime.fromISO(x.sendOn)
    const s = DateTime.now().startOf('day')
    const e = DateTime.now().endOf('day')
    return t >= s && t <= e
  })

  // ManageUrls keeps the original `setSuccessMessage`/`setErrorMessage` callback shape;
  // only the presentation moves from the in-page banner to the shared toaster.
  const notifySuccess = useCallback((message: string) => { if (message) toast.success(message) }, [])
  const notifyError = useCallback((message: string) => { if (message) toast.error(message) }, [])

  const editEmail = (id?:number)=>{    setEditId(id)
    setShowModal(true)
  }

  const onHide = (wasSuccessful?: boolean, action?: 'created' | 'updated') => {
    setShowModal(false)
    setEditId(undefined)
    refresh()
    
    if (wasSuccessful && action) {
      if (action === 'created') {
        toast.success('Email reminder scheduled successfully!')
      } else if (action === 'updated') {
        toast.success('Email reminder updated successfully!')
      }
    }
  }

  const deleteEmail = async (id?:number) => {
    try {
      const response = await fetch(`/api/email_reminders/${id}`, {method: 'DELETE'})
      if (!response.ok) {
        throw new Error('Failed to delete email reminder')
      }
      toast.success('Email reminder deleted successfully!')
      refresh()
    } catch (error) {
      toast.error(`Failed to delete email reminder: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const sendNow = async (id: number) => {
    try {
      const response = await fetch(`/api/email_reminders/${id}/send_now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `Send failed: ${response.status}`)
      }
      toast.success('Email sent immediately! Recipients will receive it shortly.')
      setTimeout(refresh, 1000)
    } catch (error) {
      throw error // re-throw so EmailReminderItem.handleSendNow can display it
    }
  }

  const sendUnset = async () => {
    try {
      const response = await fetch(`/api/email_reminders/send_todays`, {method: 'POST'})
      if (!response.ok) {
        throw new Error('Failed to send today\'s reminders')
      }
      toast.success('Today\'s email reminders are being sent!')
      setTimeout(refresh, 1000)
    } catch (error) {
      toast.error(`Failed to send today's reminders: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  const getBannerContent = () => {
    switch (activeTab) {
      case 'schedule':
        return (
          <>
            <strong>Email Reminders:</strong>
            <ul className="email-reminders-help-list">
              <li>Schedule email reminders for CLEAR member groups and chapters</li>
              <li>Emails are sent from clear@comcast.com with recipients BCC'd for privacy</li>
              <li>Messages are delivered at the scheduled date and time based on your local timezone</li>
              <li>Use the "Send Today's Reminders" button to immediately send any unsent reminders scheduled for today</li>
              <li>View pending or sent email reminders using the tabs below</li>
            </ul>
          </>
        )
      case 'urls':
        return (
          <>
            <strong>Manage URLs:</strong>
            <ul className="email-reminders-help-list">
              <li>Create and manage reusable URLs that can be included in email reminders</li>
              <li>URLs can link to important resources, forms, or documentation</li>
              <li>Each URL has a custom name for easy reference when composing email reminders</li>
              <li>Edit or delete URLs as needed to keep your library up to date</li>
            </ul>
          </>
        )
      default:
        return null
    }
  }
  
  return (
    <AdminPane as="div" width="narrow" className="email-reminders">
      <EmailModal show={showModal} onHide={onHide} editId={editId}
        recipientOptions={recipientOptions}
        chapterOptions={chapterOptions}
        urlOptions={transformedUrlOptions}
        onUrlCreated={refreshUrls}
        onDelete={deleteEmail}
      />

      <PrismBanner tone="info" className="email-reminders-help">
        {getBannerContent()}
      </PrismBanner>

      <div className="tabs">
        <PrismButton
          tone={activeTab === 'schedule' ? 'success' : 'secondary'}
          display="fill"
          onPrismClick={() => setActiveTab('schedule')}
        >
          Manage Email Reminders
        </PrismButton>
        <PrismButton
          tone={activeTab === 'urls' ? 'success' : 'secondary'}
          display="fill"
          onPrismClick={() => setActiveTab('urls')}
        >
          Manage URLs
        </PrismButton>
      </div>

      {activeTab === 'schedule' && (
        <div className="tab-content">
          
          <div className='email-reminders-toolbar'>
            <PrismButton tone="primary" display="fill" onPrismClick={()=>editEmail()}>
              Schedule an Email Reminder
            </PrismButton>
            {all.length > 0 && (
              <div className="email-reminders-filter">
                <PrismButton
                  tone={!showSent ? 'success' : 'secondary'}
                  display="fill"
                  onPrismClick={() => setShowSent(false)}
                >
                  Pending
                </PrismButton>
                <PrismButton
                  tone={showSent ? 'success' : 'secondary'}
                  display="fill"
                  onPrismClick={() => setShowSent(true)}
                >
                  Sent
                </PrismButton>
              </div>
            )}
          </div>

          {all.length == 0 ? <h5 className='email-reminders-empty'>No email reminders have been scheduled.</h5> : <>
            {shown.length > 0 ? undefined : <h5 className='email-reminders-empty'>
              {showSent ? `No emails have been sent.` : `There are no pending emails.`}
            </h5>}
            <ul className='email-reminders-list'>
              {shown.map(x=>{
                return <li key={x.id} className='email-reminders-list-item'>
                  <EmailReminderItem
                    emailReminder={x}
                    onClick={()=>editEmail(x.id)}
                    onDelete={()=>deleteEmail(x.id)}
                    onSendNow={sendNow}
                    recipientOptions={recipientOptions}
                    chapterOptions={chapterOptions}
                    urlOptions={urlOptions}
                  />
                </li>
              })}
            </ul>

            {(anyUnsentToday && !showSent) ? <>
              <hr/>
              Send any of today&apos;s unsent email reminders immediately<br/>
              <PrismButton className='email-reminders-send-today' tone="primary" display="fill" onPrismClick={sendUnset}>
                Send Today&apos;s Reminders
              </PrismButton>
            </> : undefined}
          </>}
        </div>
      )}

      {activeTab === 'urls' && (
        <div className="tab-content">
          <ManageUrls 
            setSuccessMessage={notifySuccess}
            setErrorMessage={notifyError}
            onUrlsChanged={refreshUrls}
          />
        </div>
      )}
    </AdminPane>
  )
}

export default EmailReminders